import { FC, createContext, useContext, useCallback, useEffect } from 'react';

import {
  DataPaginateResponse,
  PaginationRequestParams,
  ErrorResponse,
} from '~globals/types/api';
import { Nullable } from '~globals/types/commons';
import {
  useParseFiltersRequestSearchItems,
  useDataTableRequest,
  useToggleDialogConfirmation,
} from '~hooks/index';
import { ItemsSearchFilters } from '~hooks/useItemsSearchFilters';
import { searchItemsV2 } from '~services/item';
import {
  ItemFilterV2Base,
  ItemExtended,
  ItemsSearchHead,
} from '~services/item/types';

import { useDashboardVariantV2GiverContext } from '../../../Context';
// eslint-disable-next-line max-len
import { useDashboardVariantV2GiverInTransitFiltersContext } from '../../../InTransitFiltersContext';

import {
  DashboardVariantV2GiverContentInTransitTabContextValue,
  DashboardVariantV2GiverContentInTransitTabDialogDetailsOrderData,
} from './types';
import {
  DEFAULT_PAGINATION,
  DEFAULT_FILTERS_PAYLOAD,
  DEFAULT_ROUTE_ITEM_STATES,
  DEFAULT_GIVER_COLLECT_ITEM_STATES,
} from './utils';

const DashboardVariantV2GiverContentInTransitTabContext =
  createContext<DashboardVariantV2GiverContentInTransitTabContextValue>({
    parseFiltersRequest: () => DEFAULT_FILTERS_PAYLOAD,
    table: {
      data: [],
      stats: null,
      loading: true,
      error: null,
      reloadRequest: () => {},
      totalSize: 0,
      queryParams: {
        pagination: DEFAULT_PAGINATION,
        filters: DEFAULT_FILTERS_PAYLOAD,
      },
      handleInitialData: () => {},
      handleChangeTable: () => {},
      handleInitialTable: () => {},
    },
    actions: {
      reloadDataAndResetSelectedRows: () => {},
      handleForceRequests: () => {},
    },
    dialogs: {
      detailsOrder: {
        data: null,
        open: false,
        handleOpen: () => {},
        handleClose: () => {},
      },
    },
  });

export const useDashboardVariantV2GiverContentInTransitTabContext =
  (): DashboardVariantV2GiverContentInTransitTabContextValue =>
    useContext(DashboardVariantV2GiverContentInTransitTabContext);

const DashboardVariantV2GiverContentInTransitTabProvider: FC = ({
  children,
}) => {
  const {
    activeTab: { currentTab },
    statistics: { reloadData: reloadStatistics },
  } = useDashboardVariantV2GiverContext();
  const { filters, forceRequest } =
    useDashboardVariantV2GiverInTransitFiltersContext();

  const parseFiltersRequest = useParseFiltersRequestSearchItems({
    filters: filters.filters,
    activeFilterTabId: currentTab,
    defaultRouteItemState: DEFAULT_ROUTE_ITEM_STATES,
    defaultGiverCollectItemStateTypesId: DEFAULT_GIVER_COLLECT_ITEM_STATES,
  });

  const table = useDataTableRequest<
    PaginationRequestParams<ItemFilterV2Base>,
    DataPaginateResponse<ItemExtended[], null, ItemsSearchHead>,
    ErrorResponse
  >({
    request: searchItemsV2,
    payload: {
      pagination: DEFAULT_PAGINATION,
      filters: parseFiltersRequest(),
    },
  });
  const handleForceRequests = useCallback(
    (currentFilters?: Partial<ItemsSearchFilters>) => {
      table.handleChangeTable('filter', {
        pagination: {
          ...table.queryParams.pagination,
          page: 1,
        },
        filters: parseFiltersRequest(currentFilters),
      });

      forceRequest.toggleValue(false);
    },
    [forceRequest, parseFiltersRequest, table],
  );

  useEffect(() => {
    if (forceRequest.value) {
      handleForceRequests();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceRequest.value]);

  const reloadDataAndResetSelectedRows = useCallback(() => {
    table.reloadRequest({
      pagination: table.queryParams.pagination,
      filters: parseFiltersRequest(),
    });

    reloadStatistics();
  }, [reloadStatistics, table, parseFiltersRequest]);

  const [
    openDialogDetailsOrder,
    selectedForDetailsOrder,
    handleOpenDialogDetailsOrder,
    handleCloseDialogDetailsOrder,
  ] =
    useToggleDialogConfirmation<
      Nullable<DashboardVariantV2GiverContentInTransitTabDialogDetailsOrderData>
    >(null);

  return (
    <DashboardVariantV2GiverContentInTransitTabContext.Provider
      value={{
        parseFiltersRequest,
        table,
        actions: {
          reloadDataAndResetSelectedRows,
          handleForceRequests,
        },
        dialogs: {
          detailsOrder: {
            open: openDialogDetailsOrder,
            data: selectedForDetailsOrder,
            handleOpen: handleOpenDialogDetailsOrder,
            handleClose: handleCloseDialogDetailsOrder,
          },
        },
      }}
    >
      {children}
    </DashboardVariantV2GiverContentInTransitTabContext.Provider>
  );
};

export default DashboardVariantV2GiverContentInTransitTabProvider;
